import { WAVE_DEV, WAVE_PROD, WAVE_TEST } from '@anm/constants/domains';
import getDestinations from '@anm/data/streaming/destinations';
import isStudio from '@anm/helpers/is/isStudio';
import { isDev, isProd } from '@anm/shared/mode';
import { StreamingNetwork } from 'types/stream';

import { GetDestinationListProps, GetDestinationUrlProps, GetInstructionsLinkProps, OpenPopupProps } from '../types';

import getDestinationStorageKey from './getDestinationStorageKey';

const callbackUrl = `${isDev ? WAVE_DEV : isProd ? WAVE_PROD : WAVE_TEST}add-destination-callback`;

export const getDestinationUrl = ({
  title,
  network,
  waveApiUrl,
  isAdminOnly,
  isReconnect = false
}: GetDestinationUrlProps) => {
  const destinationType = isAdminOnly ? 'group_admin' : title.split(' ')[1];
  const typeParam = destinationType ? `=${destinationType}` : '';
  const reconnectParam = isReconnect ? encodeURIComponent(`&reconnect`) : '';

  const appParam = encodeURIComponent(isStudio() ? '&studio' : '&website');

  return `${
    getDestinations({ baseUrl: waveApiUrl }).find(d => d.title === title)?.url
  }${callbackUrl}?${network.toLowerCase()}${typeParam}${appParam}${reconnectParam}`;
};

const POPUP_WIDTH = 640;
const PADDINGS = 40;
const POPUP_HEIGHT = 640;
const TWITCH_POPUP_HEIGHT = 850;

export const getPopupOptions = (network: StreamingNetwork) => {
  const windowWidth = document.documentElement.offsetWidth;
  const windowHeight = document.documentElement.offsetHeight;

  const calculatedPopupHeight = network === 'TWITCH' ? TWITCH_POPUP_HEIGHT : POPUP_HEIGHT;
  const popupHeight = calculatedPopupHeight < windowHeight - PADDINGS ? calculatedPopupHeight : windowHeight - PADDINGS;

  const popupLeftPosition = Math.floor(windowWidth / 2 - POPUP_WIDTH / 2);
  const popupTopPosition = Math.floor((windowHeight - popupHeight) / 2) + PADDINGS / 2;

  return `popup,left=${popupLeftPosition}px, top=${popupTopPosition}px,width=${POPUP_WIDTH}px, height=${popupHeight}px, resizable`;
};

export const openPopup = ({ link, network }: OpenPopupProps) =>
  window.open(link, getDestinationStorageKey(), getPopupOptions(network));

export const getDestinationList = ({
  waveApiUrl,
  isReconnect = false,
  onRTMPClick,
  onFacebookClick,
  onAmazonLiveClick,
  onFacebookGroupClick
}: GetDestinationListProps) => {
  const ytLink = getDestinationUrl({
    title: 'YouTube',
    network: 'YOUTUBE',
    isReconnect,
    waveApiUrl
  });
  const twLink = getDestinationUrl({
    title: 'Twitch',
    network: 'TWITCH',
    isReconnect,
    waveApiUrl
  });
  const lnPageLink = getDestinationUrl({
    title: 'Linkedin page',
    network: 'LINKEDIN',
    isReconnect,
    waveApiUrl
  });
  const lnProfileLink = getDestinationUrl({
    title: 'Linkedin profile',
    network: 'LINKEDIN',
    isReconnect,
    waveApiUrl
  });

  const onTwitchClick = () => openPopup({ link: twLink, network: 'TWITCH' });
  const onYoutubeClick = () => openPopup({ link: ytLink, network: 'YOUTUBE' });
  const onLinkedinPageClick = () => openPopup({ link: lnPageLink, network: 'LINKEDIN' });
  const onLinkedinProfileClick = () => openPopup({ link: lnProfileLink, network: 'LINKEDIN' });

  return getDestinations({
    baseUrl: waveApiUrl,
    onRTMPClick,
    onTwitchClick,
    onYoutubeClick,
    onFacebookClick,
    onAmazonLiveClick,
    onLinkedinPageClick,
    onFacebookGroupClick,
    onLinkedinProfileClick
  });
};

export const getInstructionsLink = ({ network, waveUrl }: GetInstructionsLinkProps) =>
  ({
    YOUTUBE_NOT_VERIFIED: `${waveUrl}how-to-enable-live-streaming-on-youtube`,
    YOUTUBE_NO_CHANNEL: `${waveUrl}how-to-add-youtube-channel`,
    LINKEDIN: `${waveUrl}how-to-enable-live-streaming-on-linkedin`,
    FACEBOOK: `${waveUrl}how-to-enable-live-streaming-on-facebook`
  }[network]);
